import queryString from 'query-string';
import type {
    Chain,
    PlacerArrayResponse,
    PlacerEntityWrapper,
} from '@placer-ui/types';
import { FetchProps, fetchWithBasePath, fetchWrapper } from 'API/authentification/fetch-wrapper';
import { UserConfigSettingsResponse, UserSettingsResponse } from 'core/entities/user/user-settings';
import type {
    UserCustomSettings,
    UserCustomSettingsResponse,
} from 'core/entities/user/user-custom-settings';
// eslint-disable-next-line max-len
import { TagsSectionNameType } from 'features/chains/regular-view/components/search/components/common/tags-section-body/tags-section-body';
import { getAppGatewayEndpoint } from 'core/default-endpoint-url/default-endpoint-url';
import { UsersInAccountResponse } from 'API/user-api/types';

export type GetChainsSearchTagsProps = {
    searchQuery?: string;
    sectionType?: TagsSectionNameType;
    skip?: number;
};

export const TAGS_LIMIT = 12;

const basicTagsQueryParams = {
    limit: TAGS_LIMIT,
};

export const userAPI = {
    getChainsSearchTags({
        searchQuery,
        skip,
        sectionType,
    }: GetChainsSearchTagsProps): PlacerArrayResponse<PlacerEntityWrapper<Chain>[]> {
        const url = `/tags?${queryString.stringify(
            {
                ...basicTagsQueryParams,
                term: searchQuery,
                skip: skip,
                filter_mode: sectionType === 'my-tags' ? 'my' : 'company',
            },
            {
                skipEmptyString: true,
            },
        )}`;

        return fetchWrapper({
            targetUrl: url,
            basePath: `${process.env.REACT_APP_CHAINS_SERVICE_PATH}`,
            endpoint: getAppGatewayEndpoint(),
        });
    },
    getSettings(): Promise<UserSettingsResponse> {
        const url = '/user/settings';

        return fetchWrapper({ targetUrl: url });
    },
    getCustomSettings(token?: string): Promise<UserCustomSettingsResponse> {
        const url = '/user/custom_settings';
        const requestParams: FetchProps<{}> = {
            targetUrl: url,
        };

        if (token) requestParams['customToken'] = token;

        return fetchWrapper(requestParams);
    },
    setUserCustomSettings(customSettings: UserCustomSettings, token?: string) {
        const url = '/user/custom_settings';
        const method = 'POST';

        const requestParams: FetchProps<{}> = {
            targetUrl: url,
            body: customSettings,
            method,
        };

        if (token) requestParams['customToken'] = token;

        return fetchWrapper(requestParams);
    },
    getSettingsConfig(): Promise<UserConfigSettingsResponse> {
        const url = '/user/config';

        return fetchWrapper({ targetUrl: url });
    },
    async updateCompanyName(companyName: string) {
        const url = '/user/update_user_data';
        const basePath = '/2/dashboard';

        const options = {
            method: 'POST',
            body: JSON.stringify({
                company: companyName,
            }),
        };

        return fetchWithBasePath({
            passedURL: url,
            options,
            basePath,
        })
            .then((response) => response)
            .catch((error) => error);
    },
    getUsersInAccount() {
        return fetchWrapper<UsersInAccountResponse>({
            targetUrl: '/current-account/users',
        });
    },
};
